import {FileshelfItem} from './shared.electron-utils';
import {
  CommonMessage,
  CommonMessageResponse,
  CommonMessageType,
  OnlyCertainTyped,
} from './shared.types';

export {CommonMessageType, WindowFrameName} from './shared.types';

export type {
  CancelExchange,
  ExchangeOptions,
  ExchangeRequest,
  LanguageMessage,
  PollOptions,
} from './shared.types';

export enum BackupShelfType {
  UPLOAD_FAILED = 'UPLOAD_FAILED',
  UNKNOWN_FILE = 'UNKNOWN_FILE',
  VERSION_CONFLICT = 'VERSION_CONFLICT',
}

// These urls are also used to detect if we are on a login related page in browser\main.ts isAuthWorkFlowPage
export enum AuthUrls {
  LOGIN = '/login/',
  PW_RESET = '/pwreset/',
  OAUTH_REDIRECT = '/oauth-redirect/',
}

export type FilePathWithMeta = {
  filePath: string;
  createdAt?: string;
  modifiedAt?: string;
};

export type BackupShelfItem = {
  path: string;
  fileName: string;
  errorState: BackupShelfType;
  lastModifiedAt: number;
};

export type ClientSidePdfGenerationResponse = {
  success: boolean;
};

export type ClientSideTemporaryPdfGenerationResponse = {
  temporaryDocumentId: string | null;
};

export type VersionInfo = {
  version: string;
  updateDownloaded: boolean;
};

export type DirectUploadState =
  | 'UPLOADING'
  | 'IMPORTING'
  | 'COMPLETED'
  | 'FAILED'
  | 'ABORTED';

export type DirectUploadProgress = {
  entryId: string;
  documentCount: number | null;
  uploadedCount: number;
  state: DirectUploadState;
};

/**
 * IMPORTANT! IMPORTANT! IMPORTANT! IMPORTANT! IMPORTANT!
 *
 * If you add a new message type here, you also need to add it to the
 * `validChannels` list in preload.ts of the electron app.
 */
export enum BrowserMessageReceiveType {
  PING = 'ping',
  CLOSE_REQUESTED = 'close-requested',
  CLOSE_PREVENTED_BY_OPEN_FILES = 'close-prevented-by-open-files',
  RELOAD_REQUESTED = 'reload-requested',
  SHIFT_RELOAD_REQUESTED = 'shift-reload-requested',
  EXTERNAL_DOCUMENT_OPEN = 'external-document-open',
  UPDATE_AVAILABLE = 'update-available',
  UNSAVED_CHANGES_QUERY = 'unsaved-changes-query',
  DIRECT_UPLOAD_PROGRESS = 'direct-upload-progress',
}

export type BrowserMessageReceiveResponse = {
  [BrowserMessageReceiveType.PING]: undefined;
  [BrowserMessageReceiveType.CLOSE_REQUESTED]: undefined;
  [BrowserMessageReceiveType.CLOSE_PREVENTED_BY_OPEN_FILES]: undefined;
  [BrowserMessageReceiveType.RELOAD_REQUESTED]: undefined;
  [BrowserMessageReceiveType.SHIFT_RELOAD_REQUESTED]: undefined;
  [BrowserMessageReceiveType.EXTERNAL_DOCUMENT_OPEN]: {
    casusId: string;
    folderId?: string;
    documentId: string;
  };
  [BrowserMessageReceiveType.UPDATE_AVAILABLE]: VersionInfo;
  [BrowserMessageReceiveType.UNSAVED_CHANGES_QUERY]: undefined;
  [BrowserMessageReceiveType.DIRECT_UPLOAD_PROGRESS]: DirectUploadProgress;
};

export enum BrowserMessageType {
  PONG = 'pong',
  OPEN_FILE = 'open-file-command',
  FILE_SHELF_DELETE_FILES = 'delete-files-in-shelf',
  FILE_SHELF_DOWNLOAD_FILES = 'download-to-shelf',
  FILE_SHELF_GET_FILES = 'get-files-in-shelf',
  BACKUP_SHELF_DELETE_FILES = 'delete-files-in-backup-shelf',
  BACKUP_SHELF_GET_FILES = 'get-files-in-backup-shelf',
  STALE_DOCUMENT_IDS_QUERY = 'get-stale-document-ids-query',
  GET_WATCHED_DOCUMENT_IDS = 'get-watched-document-ids',
  FORCE_EXIT_APPLICATION = 'exit-anyway',
  SAFE_EXIT_APPLICATION = 'exit-safely',
  SAFE_EXIT_APPLICATION_WITHOUT_WEB_CHECK = 'exit-safely-without-web-check',
  RELOAD_WITHOUT_WEB_CHECK = 'reload-without-web-check',
  SHIFT_RELOAD_WITHOUT_WEB_CHECK = 'shift-reload-without-web-check',
  SYNC_UPLOAD_FILE_META_DATA = 'sync-files-with-meta',
  START_DIRECT_UPLOAD = 'start-direct-upload',
  ABORT_DIRECT_UPLOAD = 'abort-direct-upload',
  OPEN_TEMPORARY_EMAIL = 'open-temporary-email-command',
  OPEN_FILE_READ_ONLY = 'open-file-readonly-command',
  LEXIAN_BROWSER_VERSION = 'lexian-browser-version',
  LEXIAN_BROWSER_CAPABILITIES = 'lexian-browser-capabilities',
  SEND_USER_INFO = 'user-info',
  START_BULK_IMPORT = 'start-casus-bulk-import-command',
  FILE_OPENED = 'file-opened',
  TRIGGER_CLIENT_SIDE_PDF_GENERATION = 'trigger-client-side-pdf-generation',
  TRIGGER_CLIENT_SIDE_TEMPORARY_PDF_GENERATION = 'trigger-client-side-temporary-pdf-generation',
  COMPARE_DOCUMENTS = 'compare-documents',
  CLEAR_ELECTRON_CACHE = 'clear-electron-cache',
  UPDATE_APPLICATION = 'update-application',
  UNSAVED_CHANGES_RESPONSE = 'unsaved-changes-response',
}

export type BrowserMessageResponse = {
  [BrowserMessageType.LEXIAN_BROWSER_VERSION]: {version: string};
  [BrowserMessageType.LEXIAN_BROWSER_CAPABILITIES]: {directUpload: boolean};
  [BrowserMessageType.FILE_OPENED]: {fileId: string};
  [BrowserMessageType.SYNC_UPLOAD_FILE_META_DATA]: FilePathWithMeta[];
  [BrowserMessageType.STALE_DOCUMENT_IDS_QUERY]: string[];
  [BrowserMessageType.GET_WATCHED_DOCUMENT_IDS]: string[];
  [BrowserMessageType.BACKUP_SHELF_GET_FILES]: BackupShelfItem[];
  [BrowserMessageType.TRIGGER_CLIENT_SIDE_PDF_GENERATION]: ClientSidePdfGenerationResponse;
  [BrowserMessageType.TRIGGER_CLIENT_SIDE_TEMPORARY_PDF_GENERATION]: ClientSideTemporaryPdfGenerationResponse;
} & CommonMessageResponse;

export type BrowserMessage =
  | {payload: null; type: BrowserMessageType.PONG}
  | {
      payload: {
        remotePath: string;
        handle: string;
        fileName?: string;
      };
      type: BrowserMessageType.OPEN_FILE_READ_ONLY;
    }
  | {
      payload: {
        casusId: string;
        documentId: string;
        fileName?: string;
      };
      type: BrowserMessageType.OPEN_FILE;
    }
  | {payload: string[]; type: BrowserMessageType.FILE_SHELF_DELETE_FILES}
  | {payload: string[]; type: BrowserMessageType.BACKUP_SHELF_DELETE_FILES}
  | {
      payload: null;
      type: BrowserMessageType.FORCE_EXIT_APPLICATION;
    }
  | {
      payload: null;
      type: BrowserMessageType.SAFE_EXIT_APPLICATION;
    }
  | {
      type: BrowserMessageType.SAFE_EXIT_APPLICATION_WITHOUT_WEB_CHECK;
      payload: null;
    }
  | {
      type: BrowserMessageType.RELOAD_WITHOUT_WEB_CHECK;
      payload: null;
    }
  | {
      type: BrowserMessageType.SHIFT_RELOAD_WITHOUT_WEB_CHECK;
      payload: null;
    }
  | {
      payload: {
        [documentId: string]: FileshelfItem;
      };
      type: BrowserMessageType.FILE_SHELF_DOWNLOAD_FILES;
    }
  | {
      payload: null;
      type: BrowserMessageType.LEXIAN_BROWSER_VERSION;
    }
  | {
      payload: null;
      type: BrowserMessageType.LEXIAN_BROWSER_CAPABILITIES;
    }
  | {
      payload: null;
      type: BrowserMessageType.FILE_OPENED;
    }
  | {
      payload: {
        filePaths: string[];
      };
      type: BrowserMessageType.SYNC_UPLOAD_FILE_META_DATA;
    }
  | {
      payload: {
        entryId: string;
        folderId: string;
        paths: string[];
      };
      type: BrowserMessageType.START_DIRECT_UPLOAD;
    }
  | {
      payload: {
        entryId: string;
      };
      type: BrowserMessageType.ABORT_DIRECT_UPLOAD;
    }
  | {
      payload: {
        displayName: string;
        documentSigningOptions: 'NONE' | 'SIGNING' | 'SIGNING_NO_PASSPHRASE';
        givenNames: string;
        lastNames: string;
        locale: string;
        timesheetId: string;
        userId: string;
        username: string;
      };
      type: BrowserMessageType.SEND_USER_INFO;
    }
  | {type: BrowserMessageType.FILE_SHELF_GET_FILES; payload: null}
  | {type: BrowserMessageType.BACKUP_SHELF_GET_FILES; payload: null}
  | {
      payload: {temporaryDocumentId: string};
      type: BrowserMessageType.OPEN_TEMPORARY_EMAIL;
    }
  | {payload: null; type: BrowserMessageType.STALE_DOCUMENT_IDS_QUERY}
  | {type: BrowserMessageType.GET_WATCHED_DOCUMENT_IDS; payload: null}
  | {
      type: BrowserMessageType.START_BULK_IMPORT;
      payload: {
        absoluteRootFolderPath: string;
        casusImportMode: string;
        responsiblePersonUsername: string;
        importEmailFilesAsEmails?: boolean;
        cutOffClientNameAt?: string;
        importCasusAsInactive?: boolean;
      };
    }
  | {
      payload: ClientSidePdfGenerationMessagePayload;
      type: BrowserMessageType.TRIGGER_CLIENT_SIDE_PDF_GENERATION;
    }
  | {
      payload: ClientSideTemporaryPdfGenerationMessagePayload;
      type: BrowserMessageType.TRIGGER_CLIENT_SIDE_TEMPORARY_PDF_GENERATION;
    }
  | {
      payload: [CompareDocumentInfo, CompareDocumentInfo];
      type: BrowserMessageType.COMPARE_DOCUMENTS;
    }
  | {
      payload: {
        includeCookies: boolean;
      };
      type: BrowserMessageType.CLEAR_ELECTRON_CACHE;
    }
  | {
      payload: null;
      type: BrowserMessageType.UPDATE_APPLICATION;
    }
  | {
      payload: {
        hasUnsavedChanges: boolean;
      };
      type: BrowserMessageType.UNSAVED_CHANGES_RESPONSE;
    }
  | CommonMessage;

export type BrowserMessageForResponse = OnlyCertainTyped<
  BrowserMessage,
  (CommonMessageType | BrowserMessageType) & keyof BrowserMessageResponse
>;

export type ClientSidePdfGenerationMessagePayload = {
  casusId: string;
  folderId: string;
  documentId: string;
  type: ClientSidePdfGenerationType;
  targetPdfStandard: PdfStandard;
};

export type ClientSideTemporaryPdfGenerationMessagePayload = {
  casusId: string;
  documentId: string;
  type: ClientSidePdfGenerationType;
  targetPdfStandard: PdfStandard;
};

export type CompareDocumentInfo = {
  casusId: string;
  documentId: string;
};

export enum ClientSidePdfGenerationType {
  DOC = 'doc',
  PPT = 'ppt',
  XLS = 'xls',
}

export type PdfStandard = 'Core' | 'Archival';
